import axios from 'axios';

/**
 * The MailerAPI class provides methods to subscribe users to newsletters
 * using a singleton pattern to ensure a single instance. It includes
 * methods for subscribing to app and services newsletters, and handles
 * API requests with error management through the APIError class.
 */
export class MailerAPI {
  /**
   * Holds a static reference to an instance of the class.
   * This allows for the implementation of the singleton pattern,
   * ensuring that only one instance of the class exists.
   */
  static instance;

  /**
   * Base path for API requests.
   */
  #basePath;

  /**
   * Initializes a singleton instance of the MailerAPI class.
   * Sets the base path for the mailer API endpoint.
   * If an instance already exists, returns the existing instance.
   */
  constructor() {
    if (MailerAPI.instance) {
      return MailerAPI.instance;
    }
    this.#basePath =
      'https://mailer-api.bether.ai/api/v1/newsletter/app/subscribe';
    MailerAPI.instance = this;
  }

  /**
   * Subscribes a user to the app newsletter.
   *
   * @param {Object} payload - The subscription data for the user.
   * @returns {Promise} A promise that resolves when the subscription is complete.
   */
  async appNewsletterSubscribe(payload) {
    return await this.#subscription(payload, 'app');
  }

  /**
   * Subscribes a user to the services newsletter.
   *
   * @param {Object} payload - The subscription details.
   * @returns {Promise} A promise that resolves when the subscription is successful.
   */
  async servicesNewsletterSubscribe(payload) {
    return await this.#subscription(payload, 'services');
  }

  /**
   * Sends a subscription request to the server with the specified payload and type.
   *
   * @param {Object} payload - The data to be sent in the subscription request.
   * @param {string} type - The type of subscription being requested.
   * @returns {Promise<void>} A promise that resolves when the subscription is complete.
   * @throws {APIError} Throws an APIError if the request fails, indicating network success and HTTP status.
   * @private
   */
  async #subscription(payload, type) {
    try {
      await axios.post(this.#basePath, payload, {
        params: { type },
      });
    } catch (error) {
      throw new APIError(error);
    }
  }
}

/**
 * Represents an error encountered during an API request.
 *
 * @class
 * @extends Error
 *
 * @param {Object} axiosError - The error object returned by Axios.
 * @param {string} [message=''] - Optional custom error message.
 *
 * @property {boolean} networkSuccess - Indicates if the network request was successful.
 * @property {string} name - The name of the error, set to 'APIError'.
 * @property {number} status - The HTTP status code from the Axios error.
 */
class APIError extends Error {
  constructor(axiosError, message = '') {
    super(message);
    this.networkSuccess = Boolean(axiosError.response);
    this.name = 'APIError';
    const { status } = axiosError;
    this.status = status;
  }
}
